import { Box, Button, Card, Divider, Typography } from '@mui/material';
import { Tooltip } from 'components/common/Tooltip';
import { IconBoldInfoCircle } from 'components/icons/components/bold/IconBoldInfoCircle';
import { IconLinearArrowRight } from 'components/icons/components/linear/IconLinearArrowRight';
import { IconLinearSmileys } from 'components/icons/components/linear/IconLinearSmileys';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import {
  NestedFiltersMenuItemBaseValueType,
  NestedFiltersMenuView,
} from 'features/nestedFilters';
import {
  SLASentimentBarChart,
  SLASentimentKeyInsightList,
} from 'features/socialListeningAnalytics';
import {
  BrandInboundFiltersInput,
  Sentiment,
  SentimentMedium,
} from 'graphql/generated';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { getSentimentLabelBySentiment } from './types';
import { useSLABrandInboundSentimentViewData } from './useSLABrandInboundSentimentViewData';

export type SentimentOption = {
  label: string;
  value: Sentiment;
};

export type MediumOption = {
  label: string;
  value: SentimentMedium;
};

// only use positive, neutral, and negative sentiments
const sentimentOptions: SentimentOption[] = [
  Sentiment.Positive,
  Sentiment.Neutral,
  Sentiment.Negative,
].map((sentiment) => ({
  label: getSentimentLabelBySentiment(sentiment),
  value: sentiment,
}));

const mediumOptions: MediumOption[] = [
  {
    label: 'In Posts',
    value: SentimentMedium.InPosts,
  },
  {
    label: 'In Comments',
    value: SentimentMedium.InComments,
  },
];

export type SLABrandInboundSentimentViewProps = {
  brandId: string;
  filters: BrandInboundFiltersInput;
  hideInPosts?: boolean;
};

export const SLABrandInboundSentimentView = ({
  brandId,
  filters,
  hideInPosts,
}: SLABrandInboundSentimentViewProps) => {
  // filter by medium and sentiment on the top of the view
  const [medium, setMedium] = useState<SentimentMedium>(
    hideInPosts ? SentimentMedium.InComments : SentimentMedium.InPosts,
  );
  const [sortBySentimentOption, setSortBySentimentOption] =
    useState<SentimentOption>(sentimentOptions?.[0]);
  const [selectedMediumOption, setSelectedMediumOption] =
    useState<MediumOption>(
      mediumOptions.find((option) => option.value === medium) ||
        mediumOptions[0],
    );
  const navigate = useNavigate();

  // get the data for the sentiment bar chart and key insights based on the selected sentiment and medium
  const { slaSentimentBarChartData, slaSentimentKeyInsightListData } =
    useSLABrandInboundSentimentViewData({
      filters,
      medium,
      sortBySentiment: sortBySentimentOption.value,
      dataOptions: {
        // restrict it to show max 6 insights if there are more,
        // users can see it once they enter the brand sentiment page.
        maxKeyInsights: 6,
      },
    });

  return (
    <Card
      sx={{
        borderRadius: 5,
        boxShadow: '0px 2px 10px -3px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: theme.spacing(4.5),
          py: theme.spacing(6),
        }}
      >
        {/* Title and Info */}
        <Box display="flex" flexDirection="row" alignItems="center" gap={4}>
          <Typography
            variant="headline-sm"
            color={theme.colors?.utility[700]}
            pb={1}
            display="flex"
            alignItems="center"
            gap={2}
          >
            Brand Sentiment
            <Tooltip
              title={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: 232,
                  }}
                >
                  <Typography
                    variant="headline-xxs"
                    color={theme.colors?.primary.parchment}
                  >
                    Brand Sentiment
                  </Typography>
                  <Typography
                    variant="subhead-sm"
                    color={theme.colors?.primary.parchment}
                  >
                    Track sentiment trend for brands, products, and categories
                    to quickly identify top customer preferences and key pain
                    points.
                  </Typography>
                </Box>
              }
            >
              <Box display="flex" alignItems="center">
                <IconBoldInfoCircle
                  size={16}
                  style={{
                    rotate: '180deg',
                    width: 17,
                  }}
                />
              </Box>
            </Tooltip>
          </Typography>
        </Box>
        {/* Filter and View Analytics button */}
        <Box display="flex" alignItems="center" gap={3}>
          {/* Filter by medium */}
          <NestedFiltersMenuView
            items={[
              {
                type: 'single-select',
                key: 'medium',
                options: hideInPosts
                  ? mediumOptions.filter(
                      (option) => option.value !== SentimentMedium.InPosts,
                    )
                  : mediumOptions,
                componentsProps: {
                  label: {
                    mb: theme.spacing(1),
                  },
                },
              },
            ]}
            values={{
              medium: {
                value: selectedMediumOption,
              },
            }}
            onChange={(
              values: Record<
                string,
                NestedFiltersMenuItemBaseValueType<unknown>
              >,
            ) => {
              const newMediumOption = values?.medium?.value as MediumOption;
              setSelectedMediumOption(newMediumOption);
              setMedium(newMediumOption.value);
            }}
            componentsProps={{
              trigger: {
                text: selectedMediumOption.label,
                Icon: IconLinearSmileys,
                sx: {
                  minHeight: 42,
                  py: theme.spacing(2),
                },
              },
            }}
          />
          {/* Sort by sentiment type */}
          <NestedFiltersMenuView
            items={[
              {
                type: 'single-select',
                key: 'sentiment',
                label: 'Sort by',
                options: sentimentOptions,
                componentsProps: {
                  label: {
                    mb: theme.spacing(1),
                  },
                },
              },
            ]}
            values={{
              sentiment: {
                value: sortBySentimentOption,
              },
            }}
            onChange={(
              values: Record<
                string,
                NestedFiltersMenuItemBaseValueType<unknown>
              >,
            ) => {
              setSortBySentimentOption(
                values?.sentiment?.value as SentimentOption,
              );
            }}
            componentsProps={{
              trigger: {
                text: sortBySentimentOption.label,
                Icon: IconLinearSort,
                sx: {
                  minHeight: 42,
                  py: theme.spacing(2),
                },
              },
            }}
          />
          <Button
            size="large"
            sx={{
              ...theme.typography['headline-sm'],
              color: theme.colors?.primary.parchment,
              bgcolor: theme.colors?.primary.black,
              borderRadius: 2,
              ':hover': {
                bgcolor: theme.colors?.primary.black,
              },
              minHeight: 42,
              py: theme.spacing(2),
            }}
            onClick={() => {
              // navigate to the brand sentiment page
              // pass the filters (date range, platforms, ...) as a query param
              navigate(PlotRoutes.socialListeningSentiment(brandId), {
                state: {
                  filters,
                  sentiment: sortBySentimentOption.value,
                  medium,
                },
              });
            }}
            endIcon={
              <IconLinearArrowRight
                size={16}
                color={theme.colors?.primary.parchment}
              />
            }
          >
            View Analytics
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'row',
          padding: theme.spacing(6),
          gap: theme.spacing(6),
        }}
      >
        <Card
          sx={{
            width: 400,
            minHeight: 400,
            borderRadius: 5,
            pb: theme.spacing(8),
            boxShadow: '0px 2px 10px -3px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: theme.spacing(6),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              fontFamily="Good Sans"
              color={theme.colors?.utility[1000]}
            >
              Sentiment breakdown
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* 
              Column bar chart with 3 sentiment types:
              - Positive
              - Neutral
              - Negative
              each sentiment type has a different color,
              and the sentiment emoji is on top of the bar
              the bar is animated when the page is loaded
            */}
            <SLASentimentBarChart
              data={slaSentimentBarChartData}
              barWidth={42}
              barHeight={200}
            />
          </Box>
        </Card>
        <Card
          sx={{
            borderRadius: 5,
            p: 6,
            flex: 1,
            minHeight: 400,
            overflow: 'hidden',
            boxShadow: '0px 2px 10px -3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <SLASentimentKeyInsightList
            data={slaSentimentKeyInsightListData}
            emptyStateText="No key insights available at the moment:("
            filters={filters}
            brandId={brandId}
            medium={medium}
          />
        </Card>
      </Box>
    </Card>
  );
};
